<script lang="ts" setup>
	export type FrameClass = {
		base: string
		overflow: string
		img: Partial<{
			base: string
			width: string
			height: string
		}>
		imgWrapper: Partial<{
			base: string
			height: string
			width: string
			position: string
			rounded: string
		}>
	}

	const configClass: FrameClass = {
		base: '',
		overflow: '',
		img: {
			base: 'object-cover rounded',
			width: 'w-full h-full',
			height: 'md:max-h-[456px] max-h-[210px] md:max-h-[400px]'
		},
		imgWrapper: {
			height: '',
			width: 'w-full  md:min-w-[456px]',
			position: 'relative',
			rounded: 'rounded '
		}
	}

	const { img, imgWrapper, base, overflow } = configClass
	defineProps<{
		src: string
		alt?: string
		ui?: Partial<FrameClass>
	}>()

	const careList = [
		{
			icon: 'medic-expense',
			label: 'Medical expense covered'
		},
		{
			icon: 'baggage-delay',
			label: 'Baggage delay covered'
		},
		{
			icon: 'legal',
			label: 'Legal expense covered'
		},
		{
			icon: 'departure',
			label: 'Missed departure covered'
		}
	]
</script>

<template>
	<div :class="[ui?.base ?? base, ui?.overflow ?? overflow, 'relative']">
		<ul
			class="absolute max-h-[220px] sm:bottom-0 md:bottom-[unset] -top-10 md:-top-16 my-auto pointer-events-none pt-1 overflow-hidden wrap custom-transition bg- rounded-[6px] xl:-left-28 left-0 inline-flex h-auto flex-col z-10"
		>
			<li
				class="flex list-effect custom-transition-3 items-center justify-between rounded-t-[6px] h-9 md:h-[56px] bg-white gap-x-5 w-full px-2 md:px-5"
			>
				<div class="flex items-center gap-x-2">
					<tools-icon
						:icon="careList[0].icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[8px] font-medium text-health-900">{{ careList[0].label }} </span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="flex-none max-w-[40px] overflow-hidden min-h-5 md:min-h-[30px] min-w-[20px] px-2 relative inline-flex justify-end w-full items-end flex-col"
					>
						<slot name="right-icon">
							<span class="absolute left-0 h-full" :style="{ '--star-delay': `${2 * 3.2}s` }">
								<tools-icon dir="random" icon="travel-check" />
							</span>
							<span
								class="absolute zooming scale-0 custom-transition-3 left-0 h-full"
								:style="{ '--star-delay': `${2}s` }"
							>
								<tools-icon dir="random" icon="travel-check-scale" />
							</span>
						</slot>
					</div>
				</div>
			</li>

			<li
				v-for="(item, index) in careList.length - 1"
				:key="index"
				class="flex list-effect custom-transition-3 overflow-hidden h-0 items-center last:rounded-b-[6px] justify-between gap-x-5 w-full px-2 md:px-5 absolute active bg-white"
				:style="{ '--animate-height-duration': `${(index + 1) * 2}s` }"
			>
				<div class="flex items-center gap-x-2">
					<tools-icon
						:icon="careList[index + 1].icon"
						dir="random"
						:height="18"
						:width="18"
						svg-class="sm:w-[28px] sm:h-[28px] w-[16px] h-[16px]"
					/>
					<span class="xs:text-xs text-[6px] font-medium text-health-900">{{
						careList[index + 1].label
					}}</span>
				</div>

				<div class="flex items-center gap-x-3 flex-none">
					<div
						class="flex-none max-w-[40px] overflow-hidden min-h-5 md:min-h-[30px] min-w-[20px] px-2 relative inline-flex justify-end w-full items-end flex-col"
					>
						<span class="absolute left-0 h-full" :style="{ '--star-delay': `${2 * 3.2}s` }">
							<tools-icon dir="random" icon="travel-check" />
						</span>
						<span
							class="absolute zooming scale-0 custom-transition-3 left-0 h-full"
							:style="{ '--star-delay': `${(index + 2) * 3}s` }"
						>
							<tools-icon dir="random" icon="travel-check-scale" />
						</span>
					</div>
				</div>
			</li>
		</ul>

		<div
			:class="[
				ui?.imgWrapper?.base ?? imgWrapper.base,
				ui?.imgWrapper?.height ?? imgWrapper.height,
				ui?.imgWrapper?.position ?? imgWrapper.position,
				ui?.imgWrapper?.rounded ?? imgWrapper.rounded,
				ui?.imgWrapper?.width ?? imgWrapper.width
			]"
		>
			<nuxt-img
				provider="cloudinary"
				loading="lazy"
				:src="src"
				:alt="alt || ''"
				class="object-right object-cover"
				:class="[ui?.img?.base ?? img.base, ui?.img?.height ?? img.height, ui?.img?.width ?? img.width]"
				preload
			/>
		</div>
	</div>
</template>

<style scoped>
	:root {
		--animate-height-duration: 0;
		--animate-width-duration: 1s;
		--star-delay: 0s;
	}
	.list-effect.active {
		animation: heighten 2s linear forwards;
		animation-delay: var(--animate-height-duration);
		transition: 500ms ease-in-out;
		transform-origin: top;
		@apply h-[0] relative;
	}

	.zooming {
		transform-origin: center;
		transform: scale(0);
		animation: scale-up 1s ease-out forwards 1s alternate;
		animation-delay: var(--star-delay);
	}

	@keyframes scale-up {
		from {
			transform: scale(0) rotate(0);
		}
		to {
			transform: scale(1) rotate(6deg);
		}
	}

	@keyframes heighten {
		0% {
			height: 0;
		}
		100% {
			height: 36px;
		}
	}

	@media screen and (min-width: 768px) {
		@keyframes heighten {
			0% {
				height: 0;
			}
			100% {
				height: 56px;
			}
		}
	}
</style>
